import { useQuery } from '@tanstack/react-query';
import { Activity, AlertTriangle, Users, Zap } from 'lucide-react';
import { api } from '@/shared/api/cliente';
import { Cargando } from '@/shared/ui/Cargando';
import { Seccion } from '@/shared/ui/Seccion';
import { Evolucion, enPorcentaje } from '@/shared/ui/graficos';
import { Cifra } from '@/modules/inicio/piezas';
import type { Llamada, PanelDeTrafico as Trafico } from './tipos';

function enDiaYMes(iso: string): string {
  const [, mes, dia] = iso.split('-');
  return dia && mes ? `${dia}/${mes}` : iso;
}

function enHora(iso: string): string {
  return new Date(iso).toLocaleString('es-EC', {
    day: '2-digit',
    month: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
  });
}

function tonoDe(http: number): string {
  if (http >= 500) {
    return 'bg-red-100 text-red-700';
  }
  if (http >= 400) {
    return 'bg-amber-100 text-amber-700';
  }
  return 'bg-marca-100 text-marca-700';
}

export function PanelDeTrafico() {
  const panel = useQuery({
    queryKey: ['api-publica', 'panel'],
    queryFn: () => api.get<Trafico>('/api-publica/consumidores/panel'),
  });

  const llamadas = useQuery({
    queryKey: ['api-publica', 'llamadas'],
    queryFn: () => api.get<Llamada[]>('/api-publica/consumidores/llamadas'),
  });

  if (panel.isLoading) {
    return <Cargando titulo="Cargando" detalle="mirando quién ha llamado" />;
  }

  const datos = panel.data;
  if (!datos) {
    return null;
  }

  const variacion =
    datos.peticionesAyer > 0
      ? ((datos.peticionesHoy - datos.peticionesAyer) / datos.peticionesAyer) * 100
      : null;

  const porDia = datos.porDia.map((d) => ({ ...d, etiqueta: enDiaYMes(d.dia) }));

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
        <Cifra
          icono={Users}
          rotulo="Instituciones"
          valor={`${datos.consumidoresActivos} / ${datos.consumidores}`}
          pie="activos del total"
          tono="violeta"
        />
        <Cifra
          icono={Activity}
          rotulo="Peticiones hoy"
          valor={datos.peticionesHoy.toLocaleString('es-EC')}
          {...(variacion === null ? {} : { variacion })}
          pie={variacion === null ? 'ayer no hubo ninguna' : 'vs ayer'}
          tono="azul"
        />
        <Cifra
          icono={AlertTriangle}
          rotulo="Tasa de error 7 días"
          valor={enPorcentaje(datos.tasaDeError)}
          pie="respuestas 4xx y 5xx"
          tono={datos.tasaDeError > 5 ? 'rojo' : datos.tasaDeError > 1 ? 'ambar' : 'esmeralda'}
        />
        <Cifra
          icono={Zap}
          rotulo="Latencia media 7 días"
          valor={`${datos.latenciaMedia.toLocaleString('es-EC')} ms`}
          pie="lo que tarda el servidor"
          tono={datos.latenciaMedia > 1000 ? 'ambar' : 'pizarra'}
        />
      </div>

      <div className="grid gap-4 xl:grid-cols-3">
        <Seccion
          titulo="Llamadas por día"
          ayuda="Los últimos siete días, con los días sin tráfico en cero"
          ancho="xl:col-span-2"
        >
          <Evolucion datos={porDia} claveX="etiqueta" claveY="peticiones" nombre="Llamadas" />
        </Seccion>

        <Seccion titulo="Quién llama" ayuda="Los últimos treinta días">
          {datos.porConsumidor.length === 0 ? (
            <p className="p-6 text-center text-xs uppercase tracking-wide text-armazon-400">
              Nadie ha llamado todavía
            </p>
          ) : (
            <ul className="divide-y divide-armazon-100">
              {datos.porConsumidor.map((c, i) => (
                <li key={c.id} className="flex items-center gap-3 px-2 py-2.5">
                  <span className="grid size-6 shrink-0 place-items-center rounded-full bg-armazon-100 text-[11px] font-bold text-armazon-600">
                    {i + 1}
                  </span>
                  <span className="min-w-0 flex-1">
                    <span className="block truncate text-xs font-medium text-armazon-800">
                      {c.nombre}
                    </span>
                    <span className="block text-[11px] text-armazon-400">
                      {c.peticiones.toLocaleString('es-EC')} llamadas ·{' '}
                      {enPorcentaje(c.peticiones > 0 ? (c.errores / c.peticiones) * 100 : 0)}{' '}
                      errores · {c.latenciaMedia} ms
                    </span>
                  </span>
                </li>
              ))}
            </ul>
          )}
        </Seccion>
      </div>

      <Seccion
        titulo="Las últimas llamadas"
        ayuda="Incluidas las rechazadas: un token inválido cien veces seguidas es alguien probando"
      >
        {(llamadas.data ?? []).length === 0 ? (
          <p className="p-6 text-center text-xs uppercase tracking-wide text-armazon-400">
            Todavía no ha entrado ninguna llamada
          </p>
        ) : (
          <div className="max-h-[28rem] overflow-auto">
            <table className="w-full text-sm">
              <thead className="sticky top-0 bg-armazon-50/95">
                <tr>
                  {['Cuándo', 'Quién', 'Qué pidió', 'Identificación', 'Respuesta', 'Tardó'].map(
                    (c, i) => (
                      <th
                        key={c}
                        scope="col"
                        className={`px-3 py-2.5 text-[11px] font-semibold uppercase tracking-wider text-armazon-500 ${
                          i >= 4 ? 'text-right' : 'text-left'
                        }`}
                      >
                        {c}
                      </th>
                    ),
                  )}
                </tr>
              </thead>
              <tbody className="divide-y divide-armazon-100">
                {(llamadas.data ?? []).map((l) => (
                  <tr key={l.id} className="transition-colors hover:bg-armazon-50/60">
                    <td className="whitespace-nowrap px-3 py-2 text-[11px] tabular-nums text-armazon-500">
                      {enHora(l.ocurridoEn)}
                    </td>
                    <td className="px-3 py-2 text-xs text-armazon-700">
                      {l.consumidor ?? (
                        <span className="text-armazon-400">Sin identificar</span>
                      )}
                      {l.ip && (
                        <span className="block text-[11px] tabular-nums text-armazon-400">
                          {l.ip}
                        </span>
                      )}
                    </td>
                    <td className="px-3 py-2">
                      <code className="text-[11px] text-armazon-600">
                        {l.metodo} {l.ruta}
                      </code>
                    </td>
                    <td className="px-3 py-2 text-[11px] tabular-nums text-armazon-500">
                      {l.identificacion ?? '—'}
                    </td>
                    <td className="whitespace-nowrap px-3 py-2 text-right">
                      <span
                        className={`inline-block rounded-full px-2 py-0.5 text-[10px] font-bold ${tonoDe(l.http)}`}
                      >
                        {l.http}
                      </span>
                      {l.codigoError && (
                        <span className="block text-[10px] text-armazon-400">{l.codigoError}</span>
                      )}
                    </td>
                    <td className="px-3 py-2 text-right text-[11px] tabular-nums text-armazon-500">
                      {l.duracionMs} ms
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Seccion>
    </div>
  );
}
